import isEmpty from 'lodash/isEmpty';
import { AssetFilter } from '../context';
import { PAGINATION_DEFAULT_CONFIG } from '../config/constants';
import { Asset } from '../config/types';

/**
 * Default values for the filters that "Reset All Filters" clears. Deliberately
 * excludes pagination and multiSearchModeFilter, which survive a reset — use
 * getDefaultFilters for the complete set.
 */
export const getResettableFilterDefaults = (): Omit<
AssetFilter, 'pagination' | 'multiSearchModeFilter'
> => {
  const filters = {
    searchTags: [],
    searchText: '',
    dateFilter: [],
    assetStatusFilter: {
      ids: [],
      toggle: null,
    },
    observationTypeFilter: {},
    openedLayerId: null,
    tableFilter: {},
    tableSorter: null,
    fieldFilters: {},
    selectedLayerIds: [],
    timeTravelDate: null,
    selectedBandIds: {},
  };
  return JSON.parse(JSON.stringify(filters));
};

/**
 * The complete default AssetFilter set.
 */
export const getDefaultFilters = (): AssetFilter => ({
  ...getResettableFilterDefaults(),
  pagination: PAGINATION_DEFAULT_CONFIG,
  multiSearchModeFilter: 'or',
});

/**
 * This function is built specifically for determine if "Reset All Filters" should appear
 * Hence, this doesn't include filters that the user wouldn't expect to be reset.
 */
export const assetFilterIsActive = (assetManagerFilters: AssetFilter) => {
  if (!assetManagerFilters) return false;

  const {
    assetStatusFilter,
    tableFilter,
    openedLayerId,
    selectedLayerIds,
    fieldFilters,
    observationTypeFilter,
    dateFilter,
    selectedBandIds,
  } = assetManagerFilters;

  return !!(
    assetStatusFilter?.toggle
    || !Object.values(tableFilter || {}).every(element => element === null || !element.length)
    || openedLayerId
    || selectedLayerIds?.length
    || !isEmpty(fieldFilters)
    || !isEmpty(observationTypeFilter)
    || (dateFilter?.[0] || dateFilter?.[1])
    || !isEmpty(selectedBandIds)
  );
};

/**
 * This function detects if any filters at all are being activated.
 */
export const anyFilterIsActive = (
  assetManagerFilters: AssetFilter,
  defaultAssetTypeId: string,
  selectedAssetTypeId: string,
) => {
  if (!assetManagerFilters) return false;
  const {
    fieldFilters,
    timeTravelDate,
    pagination,
    tableSorter,
    searchTags,
    searchText,
  } = assetManagerFilters;
  return !!(
    assetFilterIsActive(assetManagerFilters)
    || (defaultAssetTypeId && (selectedAssetTypeId !== defaultAssetTypeId))
    || (fieldFilters && !isEmpty(fieldFilters))
    || timeTravelDate
    || tableSorter?.order
    || (pagination && pagination?.current !== 1)
    || (pagination && pagination?.pageSize !== PAGINATION_DEFAULT_CONFIG.pageSize)
    || searchTags?.length
    || searchText
  );
};

/**
 * Efficient way to compare two asset arrays for selectors.
 * Note: Asset dates are in string format, not date object.
 */
export const assetArrayEqual = (
  a: Asset[],
  b: Asset[],
) => a.length === b.length && a.every(
  (asset, i) => asset.id === b[i].id
  && asset.updated === b[i].updated
  // statusUpdatedDate is modified when time travelling
  && asset.statusUpdatedDate === b[i].statusUpdatedDate,
);

export const assetArrayMemoizeOptions = {
  memoizeOptions: { resultEqualityCheck: assetArrayEqual },
};
